import { DialogActions, DialogContent, Grid, Link } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { API } from '../../../global/config/api';
import { useServerCall } from '../../../global/helpers/hooks';
import { XsBaseDialog, XsButton, XsCheckbox } from '../../../global/ui';
import { ISentRecordStatusRequest } from '../../../swagger/gd/record';
import UIStore from '../../../global/stores/UIStore';
import RequestsStore from './RequestsStore';

interface IConsentForm {
  consent: boolean;
}

interface Props {
  onSent?: () => void;
}

function ConsentSentDialog({ onSent }: Props) {
  const { t } = useTranslation();

  const { control, handleSubmit, watch, reset } = useForm<IConsentForm>({
    defaultValues: { consent: false }
  });

  const consentValue = watch('consent');

  const sendCall = useServerCall<ISentRecordStatusRequest, unknown>({
    key: 'send_request',
    mode: 'control',
    request: { method: 'PUT', data: { consent_personal_data_processing: true } },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: () => {
      UIStore.setNotificationMessage(t('requests.send_success'), 'success');
      reset();
      RequestsStore.closeConsent();
      onSent?.();
    }
  });

  const handleClose = () => {
    reset();
    RequestsStore.closeConsent();
  };

  const onSubmit = handleSubmit(() => {
    const requestId = RequestsStore.consentRequestId;
    if (!requestId) return;
    sendCall.run({ url: API.sendRequest(requestId) });
  });

  return (
    <XsBaseDialog
      open={RequestsStore.isConsentOpen}
      onClose={handleClose}
      title={t('requests.consent_dialog_title')}
      maxWidth='md'
      fullWidth
    >
      <DialogContent>
        <form id='consent-form' onSubmit={onSubmit}>
          <Grid container direction='column' spacing={2} className='pt-2'>
            <Grid item>
              {t('requests.consent_personal_data_text')}{' '}
              <Link
                className='pointer'
                underline='always'
                onClick={() => window.open('https://www.siea.sk/o-siea/ochrana-osobnych-udajov/', '_blank')}
              >
                https://www.siea.sk/o-siea/ochrana-osobnych-udajov/
              </Link>
            </Grid>
            <Grid item>
              <Controller
                name='consent'
                control={control}
                render={({ field }) => (
                  <XsCheckbox controllerProps={field} label={t('requests.consent_checkbox_label')} />
                )}
              />
            </Grid>
            <Grid item>
              <span style={{ color: 'red' }}>{t('requests.consent_required_warning')}</span>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent='space-between' style={{ padding: '0 8px 8px' }}>
          <Grid item>
            <XsButton
              label='requests.cancel_button'
              variant='contained'
              icon={<i className='fal fa-times' />}
              onClick={handleClose}
            />
          </Grid>
          <Grid item>
            <XsButton
              type='submit'
              form='consent-form'
              label='requests.continue_button'
              variant='contained'
              icon={<i className='fal fa-arrow-right' />}
              disabled={!consentValue || sendCall.isPending}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </XsBaseDialog>
  );
}

export default observer(ConsentSentDialog);
